/**
 * User settings + vehicle profiles, persisted to localStorage.
 *
 * Vehicle dimensions are passed straight through to the Mapbox Directions API
 * (`max_height`, `max_width`, `max_weight`), which routes the vehicle away from
 * roads carrying a lower posted limit. That is what keeps the Hino off low
 * bridges and weight-restricted sois.
 */

export type VehicleKind = 'car' | 'truck';

export interface VehicleProfile {
  id: string;
  name: string;
  kind: VehicleKind;
  /** metres */
  height?: number;
  /** metres */
  width?: number;
  /** metric tonnes */
  weight?: number;
  /** litres per 100 km, used only for the trip log */
  consumption?: number;
  /**
   * This vehicle carries an Easy Pass / M-Pass transponder.
   *
   * Per vehicle rather than per account, because the truck and the car do not
   * necessarily both have one - and it changes routing: with a transponder the
   * cost of a toll is the fee, not ten minutes in the cash queue, so toll roads
   * stay in play and only cash-only plazas are excluded.
   */
  easyPass?: boolean;
}

export type UnitSystem = 'metric' | 'imperial';

/**
 * How much noise the app makes.
 *
 * 'alerts' exists because a full spoken instruction is often more than the
 * driver wants on a road they know - but they still want to be told when
 * something is coming. A loud chime carries that without narration.
 */
export type SoundMode = 'voice' | 'alerts' | 'muted';
export type MapTheme = 'night' | 'day' | 'auto';
export type AppLanguage = 'en' | 'th';

export interface RoutePrefs {
  avoidTolls: boolean;
  avoidMotorways: boolean;
  avoidFerries: boolean;
  avoidUnpaved: boolean;
  /** Ask Mapbox for up to 3 routes and let the driver choose. */
  alternatives: boolean;
  /** Use the live-traffic profile. Falls back to plain driving on failure. */
  useTraffic: boolean;
}

import { DEFAULT_SIGNAL_SECONDS, DEFAULT_SPEEDS, type PaceSpeeds } from '../nav/paceModel';

export interface Settings {
  units: UnitSystem;
  theme: MapTheme;
  /** UI + turn-instruction language. Mapbox generates Thai instructions natively. */
  language: AppLanguage;
  /** @deprecated superseded by `soundMode`; kept so old installs migrate. */
  voiceEnabled: boolean;
  soundMode: SoundMode;
  /** Volume of speech and chimes, 0..1 - independent of the media volume. */
  voiceVolume: number;
  /** Pause/lower Spotify while an instruction is spoken. */
  duckMusic: boolean;
  /** Keep the screen awake while navigating. */
  keepAwake: boolean;
  /** Auto zoom/pitch into junctions as they approach. */
  adaptiveCamera: boolean;
  /** Warn when exceeding this over the limit; 0 disables. km/h */
  speedWarnOffset: number;
  activeVehicleId: string;
  vehicles: VehicleProfile[];
  route: RoutePrefs;
  /** Cruising speed per road tier, km/h. Drives the ETA - see nav/paceModel.ts. */
  speeds: PaceSpeeds;
  /** Seconds lost at an average red light. Thai signal cycles are long. */
  signalSeconds: number;
  /**
   * How bad a surface has to be before the app calls it rough, 1 (only truly
   * broken roads) to 5 (mention anything). Isaan back roads are rough as a
   * baseline, so a fixed threshold either cries wolf constantly or says
   * nothing; the driver sets where normal ends.
   */
  roughSensitivity: 1 | 2 | 3 | 4 | 5;
  /** Let known-rough roads push the routing away from them, not just warn. */
  avoidRough: boolean;
  /** 24-hour clock for ETAs. Default on: it is what the dashboard shows. */
  clock24: boolean;
  /**
   * Overlay of roads we have driven: off, coloured by the speed achieved, or
   * coloured by how rough the surface was.
   */
  drivenOverlay: 'off' | 'speed' | 'rough';
  /** Cache map tiles for offline use. */
  offlineTiles: boolean;
  logTrips: boolean;
}

export const DEFAULT_VEHICLES: VehicleProfile[] = [
  {
    id: 'car',
    name: 'Car',
    kind: 'car',
    height: 1.95,
    width: 1.85,
    weight: 2.3,
    consumption: 10.5,
  },
  {
    id: 'hino',
    name: 'Hino Truck',
    kind: 'truck',
    // Conservative defaults for a Hino 300/500 series box body - edit in Settings.
    height: 3.6,
    width: 2.3,
    weight: 8.5,
    consumption: 22,
  },
];

export const DEFAULT_SETTINGS: Settings = {
  units: 'metric',
  theme: 'auto',
  language: 'en',
  voiceEnabled: true,
  soundMode: 'voice',
  voiceVolume: 1,
  duckMusic: true,
  keepAwake: true,
  adaptiveCamera: true,
  speedWarnOffset: 10,
  activeVehicleId: 'car',
  vehicles: DEFAULT_VEHICLES,
  route: {
    avoidTolls: false,
    avoidMotorways: false,
    avoidFerries: true,
    avoidUnpaved: false,
    alternatives: true,
    useTraffic: true,
  },
  speeds: DEFAULT_SPEEDS,
  signalSeconds: DEFAULT_SIGNAL_SECONDS,
  // Deliberately lenient by default: rough IS normal here, and an app that
  // says so every two kilometres gets ignored.
  roughSensitivity: 2,
  avoidRough: true,
  clock24: true,
  drivenOverlay: 'rough',
  offlineTiles: true,
  logTrips: true,
};

const KEY = 'wayfarer.settings.v1';

/**
 * The stock car profile used to be branded "Alphard". It is now the generic
 * "Car" - drivers add their own vehicles instead - so existing installs get
 * renamed in place rather than silently keeping a name nobody chose. A profile
 * the driver has already renamed themselves is left alone.
 */
function migrateVehicles(vehicles: VehicleProfile[]): VehicleProfile[] {
  return vehicles.map((v) =>
    v.id === 'alphard' ? { ...v, id: 'car', name: v.name === 'Alphard' ? 'Car' : v.name } : v,
  );
}

function merge(base: Settings, patch: unknown): Settings {
  if (!patch || typeof patch !== 'object') return base;
  const p = patch as Partial<Settings>;
  const vehicles =
    Array.isArray(p.vehicles) && p.vehicles.length ? migrateVehicles(p.vehicles) : base.vehicles;
  const activeVehicleId = p.activeVehicleId === 'alphard' ? 'car' : p.activeVehicleId;
  // Installs from before sound modes existed carry only `voiceEnabled`; keep
  // their choice rather than silently switching them back on.
  const soundMode: SoundMode =
    p.soundMode ?? (p.voiceEnabled === false ? 'muted' : base.soundMode);

  return {
    ...base,
    ...p,
    soundMode,
    roughSensitivity: (p.roughSensitivity ?? base.roughSensitivity) as Settings['roughSensitivity'],
    activeVehicleId: activeVehicleId ?? base.activeVehicleId,
    route: { ...base.route, ...(p.route ?? {}) },
    // Deep-merge so a settings blob saved before these existed still picks up
    // sane defaults instead of an undefined speed and a NaN ETA.
    speeds: { ...base.speeds, ...(p.speeds ?? {}) },
    signalSeconds:
      typeof p.signalSeconds === 'number' && p.signalSeconds >= 0
        ? p.signalSeconds
        : base.signalSeconds,
    // Never let a stored empty list orphan the vehicle picker.
    vehicles,
  };
}

/** Create a blank profile the driver can fill in. */
export function newVehicle(kind: VehicleKind = 'car'): VehicleProfile {
  const base: VehicleProfile =
    kind === 'truck'
      ? { id: '', name: 'New truck', kind, height: 3.6, width: 2.3, weight: 8.5, consumption: 22 }
      : { id: '', name: 'New vehicle', kind, height: 1.8, width: 1.8, weight: 1.8, consumption: 8 };
  return {
    ...base,
    id: `v${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`,
  };
}

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return merge(DEFAULT_SETTINGS, JSON.parse(raw));
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* private mode / quota - settings simply won't persist */
  }
}

export function activeVehicle(s: Settings): VehicleProfile {
  return s.vehicles.find((v) => v.id === s.activeVehicleId) ?? s.vehicles[0];
}
